// Which IntelliSense schema an Object Explorer "Refresh" should re-read.
//
// The explorer's Refresh used to touch only the tree's own object listings,
// leaving autocomplete on the snapshot loaded at connect time (the native
// caches have a day-scale TTL — see `snapshotLifetime.ts`), so DDL run
// outside the app stayed invisible to completions until a reconnect. Every
// Refresh below a database now refreshes that database's IntelliSense too;
// a Refresh on the connection (or its Databases folder) refreshes every
// database IntelliSense has already loaded on it.

import type { TreeNode } from '../../explorer/models/TreeModel';

export type IntelliSenseRefreshTarget =
  | { readonly kind: 'database'; readonly connectionId: string; readonly database: string }
  | { readonly kind: 'connection'; readonly connectionId: string };

type LooseMetadata = {
  connectionId?: string;
  database?: string;
  name?: string;
  status?: string;
  id?: string;
};

type LoosePinRef = { database?: string };

export function intelliSenseRefreshTargetForNode(
  node: TreeNode | undefined,
): IntelliSenseRefreshTarget | null {
  if (!node) return null;
  const metadata = (node.metadata ?? {}) as LooseMetadata;

  if (node.type === 'connection') {
    // A disconnected connection's Refresh means "reconnect" — the reconnect
    // path warms IntelliSense on its own.
    if (metadata.status === 'disconnected' || !metadata.id) return null;
    return { kind: 'connection', connectionId: metadata.id };
  }

  if (node.type === 'databases-folder') {
    return metadata.connectionId ? { kind: 'connection', connectionId: metadata.connectionId } : null;
  }

  if (node.type === 'database') {
    return metadata.connectionId && metadata.name
      ? { kind: 'database', connectionId: metadata.connectionId, database: metadata.name }
      : null;
  }

  // Pinned wrappers carry the database on their pin reference rather than
  // on the node metadata.
  const pinDatabase = (node as { pinRef?: LoosePinRef }).pinRef?.database;
  const database = metadata.database ?? pinDatabase;
  if (metadata.connectionId && database) {
    return { kind: 'database', connectionId: metadata.connectionId, database };
  }
  return null;
}
